import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  getWalletBalances, getCurrencies, 
  getScheduledTransfers, createScheduledTransfer, 
  pauseScheduledTransfer, resumeScheduledTransfer, 
  deleteScheduledTransfer, executeScheduledTransferNow,
  reactivateScheduledTransfer
} from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowUpRight, ArrowDownLeft, Loader2, 
  Calendar, Clock, Repeat, Play, Pause, Trash2, MoreVertical, Plus,
  AlertTriangle, RefreshCw, Users
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { SendMoneyTab, ReceiveMoneyTab } from '../components/transfer';
import BatchTransfer from '../components/BatchTransfer';

const Transfer = () => {
  const { user } = useAuth();
  const [currencies, setCurrencies] = useState([]);
  const [balances, setBalances] = useState([]);
  
  // Scheduled transfer state
  const [scheduledTransfers, setScheduledTransfers] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    recipient_email: '',
    amount: '',
    currency: 'USD',
    note: '',
    schedule_type: 'once',
    schedule_day: 1,
    schedule_time: '09:00',
    start_date: ''
  });
  const [creatingScheduled, setCreatingScheduled] = useState(false);

  const fetchScheduledTransfers = useCallback(async () => {
    setLoadingScheduled(true);
    try {
      const { data } = await getScheduledTransfers();
      setScheduledTransfers(data);
    } catch (error) {
      logger.error('Error fetching scheduled transfers:', error);
    } finally {
      setLoadingScheduled(false);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [currenciesRes, balancesRes] = await Promise.all([
          getCurrencies(),
          getWalletBalances()
        ]);
        setCurrencies(currenciesRes.data);
        setBalances(balancesRes.data);
      } catch (error) {
        logger.error('Error fetching data:', error);
      }
    };
    fetchData();
    fetchScheduledTransfers();
  }, [fetchScheduledTransfers]);

  const handleCreateScheduled = async () => {
    if (!scheduleForm.recipient_email || !scheduleForm.recipient_email.includes('@')) {
      toast.error('Please enter a valid recipient email');
      return;
    }
    if (!scheduleForm.amount || parseFloat(scheduleForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setCreatingScheduled(true);
    try {
      await createScheduledTransfer({
        ...scheduleForm,
        amount: parseFloat(scheduleForm.amount),
        schedule_day: scheduleForm.schedule_type === 'weekly' ? parseInt(scheduleForm.schedule_day) : 
                     scheduleForm.schedule_type === 'monthly' ? parseInt(scheduleForm.schedule_day) : null
      });
      toast.success('Scheduled transfer created successfully');
      setShowScheduleDialog(false);
      setScheduleForm({
        recipient_email: '',
        amount: '',
        currency: 'USD',
        note: '',
        schedule_type: 'once',
        schedule_day: 1,
        schedule_time: '09:00',
        start_date: ''
      });
      fetchScheduledTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create scheduled transfer');
    } finally {
      setCreatingScheduled(false);
    }
  };

  const handlePauseResume = async (transfer) => {
    try {
      if (transfer.status === 'active') {
        await pauseScheduledTransfer(transfer.id);
        toast.success('Transfer paused');
      } else if (transfer.status === 'paused') {
        await resumeScheduledTransfer(transfer.id);
        toast.success('Transfer resumed');
      }
      fetchScheduledTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const handleDelete = async (transferId) => {
    try {
      await deleteScheduledTransfer(transferId);
      toast.success('Scheduled transfer deleted');
      fetchScheduledTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleExecuteNow = async (transferId) => {
    try {
      await executeScheduledTransferNow(transferId);
      toast.success('Transfer execution started');
      fetchScheduledTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute');
    }
  };

  const handleReactivate = async (transfer) => {
    try {
      const { data } = await reactivateScheduledTransfer(transfer.id);
      toast.success(`Transfer reactivated! Next execution: ${formatNextExecution(data.next_execution)}`);
      fetchScheduledTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate transfer');
    }
  };

  const formatNextExecution = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getScheduleTypeLabel = (type) => {
    switch (type) {
      case 'once': return 'One-time';
      case 'daily': return 'Daily';
      case 'weekly': return 'Weekly';
      case 'monthly': return 'Monthly';
      default: return type;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-500/20 text-emerald-600 border-0">Active</Badge>;
      case 'paused':
        return <Badge className="bg-amber-500/20 text-amber-600 border-0">Paused</Badge>;
      case 'completed':
        return <Badge className="bg-slate-500/20 text-slate-600 border-0">Completed</Badge>;
      case 'suspended':
        return <Badge className="bg-red-500/20 text-red-600 border-0">Suspended</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-600 border-0">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Separate suspended/failed transfers from active ones
  const activeTransfers = scheduledTransfers.filter(t => !['suspended', 'failed'].includes(t.status));
  const suspendedTransfers = scheduledTransfers.filter(t => ['suspended', 'failed'].includes(t.status));

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <Layout>
      <motion.div 
        className="max-w-4xl mx-auto space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="transfer-container"
      >
        <div>
          <h1 className="text-3xl font-heading font-bold">Send & Receive</h1>
          <p className="text-muted-foreground">Transfer funds by @tag, email or phone</p>
        </div>

        <Tabs defaultValue="send" className="w-full">
          <TabsList className="grid w-full grid-cols-4 h-12 rounded-xl">
            <TabsTrigger value="send" className="rounded-lg" data-testid="send-tab">
              <ArrowUpRight className="w-4 h-4 mr-2" />
              Send
            </TabsTrigger>
            <TabsTrigger value="batch" className="rounded-lg" data-testid="batch-tab">
              <Users className="w-4 h-4 mr-2" />
              Batch
            </TabsTrigger>
            <TabsTrigger value="receive" className="rounded-lg" data-testid="receive-tab">
              <ArrowDownLeft className="w-4 h-4 mr-2" />
              Receive
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="rounded-lg" data-testid="scheduled-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Scheduled
            </TabsTrigger>
          </TabsList>

          <TabsContent value="send" className="mt-6">
            <SendMoneyTab 
              user={user} 
              balances={balances} 
              setBalances={setBalances}
              currencies={currencies}
            />
          </TabsContent>

          <TabsContent value="batch" className="mt-6">
            <BatchTransfer wallets={Object.fromEntries(balances.map(b => [b.currency, b.balance]))} />
          </TabsContent>

          <TabsContent value="receive" className="mt-6">
            <ReceiveMoneyTab user={user} />
          </TabsContent>

          <TabsContent value="scheduled" className="space-y-6 mt-6">
            {/* Header with Create Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-heading font-semibold">Scheduled Transfers</h2>
                <p className="text-sm text-muted-foreground">Automate your recurring payments</p>
              </div>
              <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
                <Button onClick={() => setShowScheduleDialog(true)} className="rounded-xl" data-testid="create-schedule-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Schedule
                </Button>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Create Scheduled Transfer</DialogTitle>
                    <DialogDescription>
                      Set up automatic transfers to happen on a schedule
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="sched_email">Recipient Email</Label>
                      <Input
                        id="sched_email"
                        type="email"
                        placeholder="recipient@example.com"
                        value={scheduleForm.recipient_email}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, recipient_email: e.target.value })}
                        className="rounded-xl"
                        data-testid="schedule-email-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="sched_amount">Amount</Label>
                        <Input
                          id="sched_amount"
                          type="number"
                          placeholder="0.00"
                          value={scheduleForm.amount}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, amount: e.target.value })}
                          className="rounded-xl"
                          min="0.01"
                          step="0.01"
                          data-testid="schedule-amount-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sched_currency">Currency</Label>
                        <Select 
                          value={scheduleForm.currency} 
                          onValueChange={(v) => setScheduleForm({ ...scheduleForm, currency: v })}
                        >
                          <SelectTrigger className="rounded-xl" data-testid="schedule-currency-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {balances.map((b) => (
                              <SelectItem key={b.currency} value={b.currency}>
                                {b.symbol} {b.currency}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sched_type">Schedule Type</Label>
                      <Select 
                        value={scheduleForm.schedule_type} 
                        onValueChange={(v) => setScheduleForm({ ...scheduleForm, schedule_type: v })}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="schedule-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="once">One-time</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {scheduleForm.schedule_type === 'weekly' && (
                      <div className="space-y-2">
                        <Label htmlFor="sched_day">Day of Week</Label>
                        <Select 
                          value={scheduleForm.schedule_day.toString()} 
                          onValueChange={(v) => setScheduleForm({ ...scheduleForm, schedule_day: parseInt(v) })}
                        >
                          <SelectTrigger className="rounded-xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {weekDays.map((day, idx) => (
                              <SelectItem key={day} value={(idx + 1).toString()}>{day}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {scheduleForm.schedule_type === 'monthly' && (
                      <div className="space-y-2">
                        <Label htmlFor="sched_day_month">Day of Month</Label>
                        <Select 
                          value={scheduleForm.schedule_day.toString()} 
                          onValueChange={(v) => setScheduleForm({ ...scheduleForm, schedule_day: parseInt(v) })}
                        >
                          <SelectTrigger className="rounded-xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[...Array(28)].map((_, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {scheduleForm.schedule_type === 'once' && (
                      <div className="space-y-2">
                        <Label htmlFor="sched_date">Schedule Date</Label>
                        <Input
                          id="sched_date"
                          type="datetime-local"
                          value={scheduleForm.start_date}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, start_date: e.target.value })}
                          className="rounded-xl"
                          data-testid="schedule-date-input"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="sched_note">Note (Optional)</Label>
                      <Textarea
                        id="sched_note"
                        placeholder="What's this for?"
                        value={scheduleForm.note}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, note: e.target.value })}
                        className="rounded-xl resize-none"
                        rows={2}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={handleCreateScheduled}
                      disabled={creatingScheduled}
                      className="w-full rounded-xl"
                      data-testid="create-schedule-submit-btn"
                    >
                      {creatingScheduled ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Calendar className="w-4 h-4 mr-2" />
                          Create Schedule
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Scheduled Transfers List */}
            {loadingScheduled ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : scheduledTransfers.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">No Scheduled Transfers</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Set up recurring payments to automate your transfers
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Suspended Transfers Alert */}
                {suspendedTransfers.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50/50">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        <CardTitle className="text-base text-amber-800">
                          {suspendedTransfers.length} Suspended Transfer{suspendedTransfers.length > 1 ? 's' : ''}
                        </CardTitle>
                      </div>
                      <CardDescription className="text-amber-700">
                        These transfers were paused due to insufficient funds. Add funds and reactivate them.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {suspendedTransfers.map((transfer) => (
                        <div 
                          key={transfer.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-white rounded-xl border border-amber-200"
                          data-testid={`suspended-transfer-${transfer.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-amber-100">
                              <AlertTriangle className="w-4 h-4 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{transfer.recipient_email}</p>
                              <p className="text-sm text-muted-foreground">
                                ${transfer.amount.toFixed(2)} {transfer.currency} • {getScheduleTypeLabel(transfer.schedule_type)}
                              </p>
                              {transfer.failure_reason && (
                                <p className="text-xs text-amber-700 mt-1">{transfer.failure_reason}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="rounded-lg border-amber-300 text-amber-700 hover:bg-amber-100"
                              onClick={() => handleReactivate(transfer)}
                              data-testid={`reactivate-btn-${transfer.id}`}
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Reactivate
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="rounded-lg text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(transfer.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Active Transfers */}
                <AnimatePresence>
                  {activeTransfers.map((transfer, index) => (
                    <motion.div
                      key={transfer.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card data-testid={`scheduled-transfer-${transfer.id}`}>
                        <CardContent className="p-6">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-start gap-4">
                              <div className="p-3 rounded-xl bg-muted">
                                <Repeat className="w-5 h-5 text-emerald-600" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-semibold">{transfer.recipient_email}</p>
                                  {getStatusBadge(transfer.status)}
                                </div>
                                <div className="text-sm text-muted-foreground space-y-1">
                                  <p className="flex items-center gap-1">
                                    <span className="font-mono font-medium text-foreground">
                                      ${transfer.amount.toFixed(2)} {transfer.currency}
                                    </span>
                                    • {getScheduleTypeLabel(transfer.schedule_type)}
                                  </p>
                                  <p className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Next: {formatNextExecution(transfer.next_execution)}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="rounded-lg">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {transfer.status !== 'completed' && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleExecuteNow(transfer.id)}>
                                      <Play className="w-4 h-4 mr-2" />
                                      Execute Now
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handlePauseResume(transfer)}>
                                      {transfer.status === 'active' ? (
                                        <>
                                          <Pause className="w-4 h-4 mr-2" />
                                          Pause
                                        </>
                                      ) : (
                                        <>
                                          <Play className="w-4 h-4 mr-2" />
                                          Resume
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(transfer.id)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* No Active Transfers Message */}
                {activeTransfers.length === 0 && suspendedTransfers.length > 0 && (
                  <Card className="text-center py-8">
                    <CardContent>
                      <Calendar className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                      <p className="text-sm text-muted-foreground">
                        No active scheduled transfers. Reactivate suspended transfers above or create a new one.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default Transfer;
